export type RequirementDomainSwatch = {
  requirement_domain_id: string;
  name: string;
  code?: string | null;
  color_hex: string;
};

type DomainLink = {
  relation?: string | null;
  domain?: {
    requirement_domain_id?: string | null;
    name?: string | null;
    code?: string | null;
    color_hex?: string | null;
    sort_order?: number | null;
  } | null;
};

type RequirementLike = {
  domains?: DomainLink[] | null;
  competencies?: Array<{
    competency?: {
      domains?: DomainLink[] | null;
    } | null;
  }> | null;
};

function normalizeHex(color?: string | null): string | null {
  if (!color) {
    return null;
  }
  const trimmed = color.trim();
  if (!trimmed) {
    return null;
  }
  const withHash = trimmed.startsWith("#") ? trimmed : `#${trimmed}`;
  if (!/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(withHash)) {
    return null;
  }
  return withHash;
}

function pushDomain(
  map: Map<string, RequirementDomainSwatch>,
  link: DomainLink | null | undefined,
) {
  const domain = link?.domain;
  const id = domain?.requirement_domain_id;
  if (!id || map.has(id)) {
    return;
  }
  map.set(id, {
    requirement_domain_id: id,
    name: domain?.name || domain?.code || "Domain",
    code: domain?.code,
    // Fall back so linked domains still count/render without a stored hex.
    color_hex: normalizeHex(domain?.color_hex) || "#9e9e9e",
  });
}

/**
 * Unique domain color swatches for a requirement.
 * Includes direct domain links and, for EPAs, domains from linked competencies.
 */
export function collectRequirementDomainSwatches(
  requirement?: RequirementLike | null,
): RequirementDomainSwatch[] {
  if (!requirement) {
    return [];
  }
  const map = new Map<string, RequirementDomainSwatch>();
  for (const link of requirement.domains || []) {
    pushDomain(map, link);
  }
  for (const row of requirement.competencies || []) {
    for (const link of row.competency?.domains || []) {
      pushDomain(map, link);
    }
  }
  return Array.from(map.values());
}

/** True when at least one requirement is linked to a domain. */
export function requirementsHaveDomainSwatches(
  requirements?: Array<RequirementLike | null | undefined> | null,
): boolean {
  return (requirements || []).some(
    (requirement) => collectRequirementDomainSwatches(requirement).length > 0,
  );
}
